'use client';

import React, { useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';

import { ImageSlide } from '@/app/components/hotel-detail/evo/Hero/EvoHeroGalleryImageSlide';

import {
  evoHeroBoxes,
  getGallerySourceOptimization,
  getHeroImageSourcePreload,
} from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { useIsLgOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { HeroNoImageAvailable } from '@/app/components/hotel-detail/HeroNoImageAvailable';
import { getPhotoAlt } from '@/app/components/hotel-detail/getPhotoAlt';
import { useIsHotelStaticLoading } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useHeroStrip } from '@/app/components/hotel-detail/hooks/useHeroStrip';
import EvoGalleryModal from '@/app/components/hotel-detail/evo/Hero/EvoGalleryModal';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import ShareButton from '@/app/components/hotel-detail/ShareButton';
import { BackArrow, BackButtonWithArrow } from '@/app/components/hotel-detail/BackButton';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

import EvoHeroGallerySliderOverlay from '@/app/components/hotel-detail/evo/Hero/EvoHeroGallerySliderOverlay';
import { EvoHeroBadges } from '@/app/components/hotel-detail/evo/Hero/EvoHeroBadges';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { URLParameters } from '@/app/common/urlParameters';
import { useTranslation } from '@/app/components/translations/Translation';
import { createDetailPageImageGalleryClickedPayload } from '@/app/common/starTrack/events/detailPageImageGalleryClicked.ts';

export type HeroGalleryProps = {
  images?: string[];
  name?: string;
} & React.HTMLAttributes<HTMLElement>;

const EvoHeroGalleryDesktopOverlay = dynamic(
  () =>
    import(
      /* webpackChunkName: "EvoHeroGalleryDesktopOverlay" */ '@/app/components/hotel-detail/evo/Hero/EvoHeroGalleryDesktopOverlay'
    ),
  {
    ssr: false,
  }
);

/**
 * The hotel type badges over the desktop hero photo. They sit next to the photo, not inside it, so
 * the photo's name (its "N photos" count) matches everything the photo shows (MPH-1522). They keep
 * the spot they had inside the count overlay, and let clicks through to the photo under them.
 */
const HeroBadgesOverPhoto = styled(EvoHeroBadges)`
  position: absolute;
  top: 16px;
  left: 16px;
  pointer-events: none;
`;

type OverlayStateType = { isOpen: boolean | undefined; initialFocussedImageIndex?: number | undefined };

/**
 * Basic cross device functionality for the gallery shown in hotel page hero.
 *
 * Renders the common parts needed for all device sizes on server side.
 * Those are basically just a list of the first 5 images that is scrollable on small devices
 * and layouted in a grid on larger screens.
 *
 * Screensize specific functionality is conditionally added or loaded on the client only at runtime.
 *
 * Adds preload of the main image to improve LCP, see:
 * - https://web.dev/preload-responsive-images/
 * - uses link imagesrcset attribute to match the same image that will be loaded in the browser
 *
 */
export default function HeroGallery(props: HeroGalleryProps) {
  const { images, name } = props;
  const { sendCuriosityClickEvent } = useCuriosity();
  const { commonData } = usePageWrapperData();
  const { isApp } = commonData;
  const { starTrack } = useCommonClient();
  const isHotelStaticLoading = useIsHotelStaticLoading();
  const initialParams = useInitialParams();
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const { t } = useTranslation();

  const isMobile = !useIsLgOrGreater(commonData.isDesktopDevice);
  const showBackArrow = !isApp && !initialParams.isHotelStaticPage && isMobile;
  const [overlayState, setOverlayState] = useState<OverlayStateType>({ isOpen: undefined });

  const firstImageRef = useRef<HTMLDivElement>(null);
  const sliderContainerRef = useRef<HTMLDivElement>(null);

  // Desktop shows the first photo with the count of the rest on it; the mobile strip swipes through
  // every photo instead, and loops both ways (MPH-1213, MPH-1237).
  const isMobileLayout = isMobile || (isMobile === undefined && commonData.deviceType === 'mobile');

  const initialImages = useMemo(() => (isMobileLayout ? images : images?.slice(0, 1)), [images, isMobileLayout]);

  const initialImagesWithSources = useMemo(
    () =>
      getGallerySourceOptimization({
        images: initialImages || [''],
        isMobile,
        isTopSecret: false,
        totalImages: initialImages?.length || 0,
        boxes: evoHeroBoxes,
      }),
    [initialImages, isMobile]
  );

  const strip = useHeroStrip(sliderContainerRef, initialImagesWithSources.length, isMobile);

  const preloadImages = initialImages
    ? getHeroImageSourcePreload({
        url: initialImages[0],
        isTopSecret: false,
        totalImages: images?.length || 0,
        boxes: evoHeroBoxes,
      })
    : null;

  if (!images?.length) return <HeroNoImageAvailable />;

  // The last slide carries the "N photos" count when there are more photos than slides: only the
  // desktop hero, which shows the first photo alone.
  const hasCountOverlay = (index: number) =>
    index === initialImagesWithSources.length - 1 && images.length > initialImagesWithSources.length;
  const photosCountLabel = t('vacation.select.cc.hotel.gallery.photos_count', { count: String(images.length) });

  async function onImageClick(index: number, hasOverlay: boolean) {
    starTrack.detailPageImageGalleryClicked(
      createDetailPageImageGalleryClickedPayload({
        searchId: searchId || '',
        productDetailKey: initialParams.productDetailKey,
      })
    );
    if (hasOverlay) {
      sendCuriosityClickEvent({
        event_name: 'click_gallery_show_all_overlay',
        event_context: 'hero_gallery',
      });
    } else {
      sendCuriosityClickEvent({
        event_name: 'click_gallery_hero_image',
        event_context: 'hero_gallery',
        event_value: `image_num_${index}`,
      });
    }
    setOverlayState({ isOpen: true, initialFocussedImageIndex: index });
  }

  return (
    <>
      {/*
        React hoists these into <head> on its own, including during the server render, which is the
        only place a preload can help LCP. They used to go through `next/head` — a Pages Router api
        that does nothing under the App Router, so the hero preload had silently stopped emitting.
      */}
      {preloadImages?.map((preloadImage) => (
        // Keyed by media as well: a Booking url without `/max500/` gives every breakpoint the same
        // href, and a key on `href` alone would then repeat (MPH-628).
        <link
          key={`${preloadImage.media ?? ''} ${preloadImage.href}`}
          rel="preload"
          as="image"
          href={preloadImage.href}
          imageSrcSet={preloadImage.imageSrcset}
          media={preloadImage.media}
          fetchPriority="high"
        />
      ))}

      <div
        css={css`
          overflow-x: auto;
          position: relative;
          height: 100%;
        `}
      >
        <div
          ref={sliderContainerRef}
          role="region"
          aria-label={t('vacation.select.evo_gallery.image_gallery') || 'Image gallery'}
          css={css`
            position: relative;
            height: 100%;
            display: flex;
            overflow-x: auto;
            overflow-y: hidden;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;

            &::scrollbar {
              display: none;
            }

            &::-webkit-scrollbar {
              display: none;
            }
          `}
        >
          {strip.hasLeadingCopy && (
            // Swiping back onto this copy of the last photo goes on to the real one (MPH-1237).
            <ImageSlide
              key="loop-copy-start"
              {...strip.getLoopCopy('start', initialImagesWithSources, (index) => onImageClick(index, false))}
              css={`
                flex: 0 0 100%;
              `}
            />
          )}
          {initialImagesWithSources.map((image, index) => (
            <ImageSlide
              containerRef={index === 0 ? firstImageRef : undefined}
              {...strip.getSlideImage(index, image)}
              /**
               * Keyed by position, not by url. The hotel's own photos replace the ones borrowed
               * from the results card, and a url key made every slide a different element, so the
               * swap remounted the strip and threw the reader back to the first photo. Keying the
               * slot keeps the element — and the scroll position — so you stay where you were and
               * that slot simply shows the photo the hotel's list has there (MPH-1002).
               */
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              role="button"
              // MPH-1522: the photo that shows the count is named after it ("100 photos"), as it opens the gallery.
              aria-label={hasCountOverlay(index) ? photosCountLabel : getPhotoAlt(t, name, index, images.length)}
              {...strip.getSlideAccessibility(index)}
              /*
               * The first slide is the hero, so it is never lazy: it is what the preload in the
               * head is for, and `loading="lazy"` on the LCP image undoes that. `!isMobile` used to
               * catch desktop and the server render alike, where it is the only slide there is.
               * On the mobile strip the photos within reach load right away, and the rest are
               * placeholders until the reader gets near them.
               */
              loading={index === 0 || (isMobile && strip.isPhotoReady(index)) ? 'eager' : 'lazy'}
              onClick={() => {
                onImageClick(index, hasCountOverlay(index));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onImageClick(index, hasCountOverlay(index));
                  return;
                }
                strip.onSlideKeyDown(index)(e);
              }}
              overlay={
                hasCountOverlay(index) ? (
                  <EvoHeroGalleryDesktopOverlay
                    counterTotalSlides={images.length}
                    currentSlide={0}
                    showCounterIcon={true}
                    css={css`
                      color: ${({ theme }) => theme.kallisto.color.text.static.textOnDark};
                      position: absolute;
                      left: 0;
                      width: 100%;
                      top: 0;
                      height: 100%;
                    `}
                  />
                ) : undefined
              }
              css={`
                flex: 0 0 100%;
              `}
            />
          ))}
          {strip.loop && (
            // Swiping onto this copy of the first photo goes back to the real one (MPH-1213).
            <ImageSlide
              key="loop-copy"
              {...strip.getLoopCopy('end', initialImagesWithSources, (index) => onImageClick(index, false))}
              css={`
                flex: 0 0 100%;
              `}
            />
          )}
          {/*
            Desktop only, on the photo that carries the count, as when they were part of that overlay.
            They stay in the "Image gallery" region, next to the photo rather than inside it; the desktop
            region holds that one photo and does not scroll, so they keep their spot on it.
          */}
          {!isMobile && hasCountOverlay(initialImagesWithSources.length - 1) && <HeroBadgesOverPhoto />}
        </div>
        {isMobile && !initialParams.isHotelStaticPage ? (
          <div
            css={css`
              position: absolute;
              top: 0;
              right: 0;
              padding: 16px;

              [class*='ShareButton__StyledMenuButton'] {
                width: 40px;
                height: 40px;
                border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
                background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
                color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};

                & svg,
                & [data-library-icon] {
                  width: 20px;
                  height: 20px;
                  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
                }
              }
            `}
          >
            <ShareButton hotelName={name || ''} className="info-share" style={{ gridArea: 'share' }} />
          </div>
        ) : null}
        {showBackArrow && (
          <div
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              padding: 16px;
            `}
          >
            <BackButtonWithArrow
              solidIcon={true}
              isEvo={true}
              css={css`
                ${BackArrow} {
                  display: flex;
                  justify-content: center;
                  border-radius: 50%;
                  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
                  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
                  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
                  width: 40px;
                  height: 40px;

                  & svg,
                  & [data-library-icon] {
                    width: 20px;
                    height: 20px;
                  }
                }
              `}
            />
          </div>
        )}
        {isMobile && images.length ? (
          <EvoHeroGallerySliderOverlay
            keepScrollPosition={true}
            initialImageIndex={0}
            scrollContainerRef={sliderContainerRef}
            amountOfSlides={initialImagesWithSources.length}
            counterTotalSlides={images ? images.length : 0}
            slideChanged={strip.onSlideChanged}
            hasLeadingCopy={strip.hasLeadingCopy}
            onCounterClick={(slideIndex) => onImageClick(slideIndex, false)}
            /*
             * Held back until the request settles, on the same flag the hero's other sections use.
             * The count is right either way, but until then it is the few photos borrowed from the
             * results card ("1 / 5") and would flip to the hotel's real total ("1 / 52") under the
             * reader a moment later.
             *
             * Once it has settled the counter shows, however it settled: on a failure the strip
             * keeps the borrowed photos and the borrowed count describes them correctly (MPH-1002).
             */
            counterDisplay={isHotelStaticLoading ? 'none' : 'default'}
            showCounterIcon={false}
            hidePagination={true}
          />
        ) : undefined}
      </div>

      <EvoGalleryModal
        images={images}
        name={name}
        initialImageIndex={overlayState.initialFocussedImageIndex}
        isOpen={overlayState.isOpen}
        onDismiss={() => setOverlayState({ isOpen: false })}
        onReopen={() => setOverlayState((prev) => ({ ...prev, isOpen: true }))}
      />
    </>
  );
}
